type AnsiPart = {
  text: string
  foreground?: string
  background?: string
  bold?: boolean
}

const ansiColors: Record<number, string> = {
  30: '#000000',
  31: '#cc0000',
  32: '#4e9a06',
  33: '#c4a000',
  34: '#3465a4',
  35: '#75507b',
  36: '#06989a',
  37: '#d3d7cf',
  90: '#555753',
  91: '#ef2929',
  92: '#8ae234',
  93: '#fce94f',
  94: '#729fcf',
  95: '#ad7fa8',
  96: '#34e2e2',
  97: '#eeeeec',
}

const ansiBackgrounds: Record<number, string> = {
  40: '#000000',
  41: '#cc0000',
  42: '#4e9a06',
  43: '#c4a000',
  44: '#3465a4',
  45: '#75507b',
  46: '#06989a',
  47: '#d3d7cf',
  100: '#555753',
  101: '#ef2929',
  102: '#8ae234',
  103: '#fce94f',
  104: '#729fcf',
  105: '#ad7fa8',
  106: '#34e2e2',
  107: '#eeeeec',
}

export const ansiParse = (text: string): AnsiPart[] => {
  const parts: AnsiPart[] = []
  // eslint-disable-next-line no-control-regex
  const ansiRegex = /\x1b\[([0-9;]*)m/g

  let lastIndex = 0
  let currentForeground: string | undefined
  let currentBackground: string | undefined
  let currentBold = false

  let match
  while ((match = ansiRegex.exec(text)) !== null) {
    // Add text before this escape sequence
    if (match.index > lastIndex) {
      parts.push({
        text: text.slice(lastIndex, match.index),
        foreground: currentForeground,
        background: currentBackground,
        bold: currentBold,
      })
    }

    // Parse the escape codes
    const codes = match[1].split(';').map(Number)
    for (const code of codes) {
      if (code === 0) {
        // Reset
        currentForeground = undefined
        currentBackground = undefined
        currentBold = false
      } else if (code === 1) {
        // Bold
        currentBold = true
      } else if (code === 22) {
        // Normal intensity (not bold)
        currentBold = false
      } else if (code >= 30 && code <= 37) {
        // Standard foreground colors
        currentForeground = ansiColors[code]
      } else if (code >= 90 && code <= 97) {
        // Bright foreground colors
        currentForeground = ansiColors[code]
      } else if (code === 39) {
        // Default foreground
        currentForeground = undefined
      } else if (code >= 40 && code <= 47) {
        // Standard background colors
        currentBackground = ansiBackgrounds[code]
      } else if (code >= 100 && code <= 107) {
        // Bright background colors
        currentBackground = ansiBackgrounds[code]
      } else if (code === 49) {
        // Default background
        currentBackground = undefined
      } else if (code === 38 || code === 48) {
        // Extended color (256 or RGB) - skip for now
        // These would need more complex parsing
      }
    }

    lastIndex = match.index + match[0].length
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      text: text.slice(lastIndex),
      foreground: currentForeground,
      background: currentBackground,
      bold: currentBold,
    })
  }

  // If no parts were created, return the original text
  if (parts.length === 0) {
    return [{ text }]
  }

  return parts
}
